import { getCameraTooltipThemeFromState, getTooltipStateThemeFromState } from './state';
import type {
  CameraTooltipThemeItem,
  MapApiCameraTooltip,
  MapApiTooltip,
  MapState,
  TooltipStateThemeItem,
} from './types';

/** Shared layout and surface classes for field and camera tooltip cards. */
const TOOLTIP_CARD_CLASS_NAME = 'w-[400px] rounded-2xl border p-4 shadow-2xl backdrop-blur';

/**
 * Clears the shared tooltip content root before rendering another tooltip variant.
 *
 * @param tooltipContent DOM node that contains current tooltip markup.
 */
function clearTooltipContent(tooltipContent: HTMLElement): void {
  tooltipContent.textContent = '';
}

/**
 * Creates the shared tooltip card shell used by field and camera popup renderers.
 *
 * @param params Card background and border colors resolved from API theme tokens.
 * @returns Tooltip article element ready for content children.
 */
function createTooltipCard(params: { surfaceColor: string; borderColor: string }): HTMLElement {
  const cardElement = document.createElement('article');
  cardElement.className = TOOLTIP_CARD_CLASS_NAME;
  cardElement.style.backgroundColor = params.surfaceColor;
  cardElement.style.borderColor = params.borderColor;

  return cardElement;
}

/**
 * Creates the field tooltip card with debt-specific content.
 *
 * @param params Field tooltip payload and resolved visual theme.
 * @returns Tooltip card ready to be inserted into the shared tooltip root.
 */
function createFieldTooltipCard(params: {
  tooltipData: MapApiTooltip;
  stateTheme: TooltipStateThemeItem;
}): HTMLElement {
  const { tooltipData, stateTheme } = params;
  const cardElement = createTooltipCard({
    surfaceColor: stateTheme.surfaceColor,
    borderColor: stateTheme.borderColor,
  });

  const titleElement = document.createElement('p');
  titleElement.className = 'text-sm font-semibold text-slate-950';
  titleElement.textContent = tooltipData.title;

  const cadastralElement = document.createElement('p');
  cadastralElement.className = 'mt-3 text-sm text-slate-700';
  cadastralElement.textContent = tooltipData.cadastralText;

  const areaElement = document.createElement('p');
  areaElement.className = 'mt-2 text-sm text-slate-700';
  areaElement.textContent = tooltipData.areaText;

  const debtElement = document.createElement('p');
  debtElement.className = 'mt-3 text-sm';
  debtElement.style.color = stateTheme.debtTextColor;
  debtElement.textContent = tooltipData.debtText;

  const contentElements = [titleElement, cadastralElement, areaElement];

  const currentChargesGroupElement = document.createElement('div');
  currentChargesGroupElement.className = 'mt-3 rounded-xl border px-3 py-2.5';
  currentChargesGroupElement.style.borderColor = `color-mix(in srgb, ${stateTheme.borderColor} 30%, transparent)`;

  for (const componentText of [
    tooltipData.currentMemberChargesText,
    tooltipData.currentTargetedChargesText,
  ]) {
    if (componentText === null) continue;
    const componentElement = document.createElement('p');
    componentElement.className =
      currentChargesGroupElement.childElementCount === 0 ? 'text-sm' : 'mt-2 text-sm';
    componentElement.style.color = stateTheme.debtTextColor;
    componentElement.textContent = componentText;
    currentChargesGroupElement.append(componentElement);
  }

  if (tooltipData.paymentDeadlineText !== null) {
    const paymentDeadlineElement = document.createElement('p');
    paymentDeadlineElement.className =
      currentChargesGroupElement.childElementCount === 0
        ? 'text-xs leading-4 opacity-75'
        : 'mt-2 border-t pt-2 text-xs leading-4 opacity-75';
    paymentDeadlineElement.style.borderColor = `color-mix(in srgb, ${stateTheme.borderColor} 24%, transparent)`;
    paymentDeadlineElement.style.color = stateTheme.debtTextColor;
    paymentDeadlineElement.textContent = tooltipData.paymentDeadlineText;
    currentChargesGroupElement.append(paymentDeadlineElement);
  }

  if (currentChargesGroupElement.childElementCount > 0) {
    contentElements.push(currentChargesGroupElement);
  }

  contentElements.push(debtElement);

  if (tooltipData.penaltiesText !== null) {
    const penaltiesElement = document.createElement('p');
    penaltiesElement.className = 'mt-2 text-sm';
    penaltiesElement.style.color = stateTheme.debtTextColor;
    penaltiesElement.textContent = tooltipData.penaltiesText;
    contentElements.push(penaltiesElement);
  }

  if (tooltipData.overallDebtText !== null) {
    const overallDebtElement = document.createElement('p');
    overallDebtElement.className =
      'mt-3 py-2 -mx-4 px-4 rounded-2xl border-y text-sm font-semibold';
    overallDebtElement.style.borderColor = `color-mix(in srgb, ${stateTheme.borderColor} 30%, transparent)`;
    overallDebtElement.style.color = stateTheme.debtTextColor;
    overallDebtElement.textContent = tooltipData.overallDebtText;
    contentElements.push(overallDebtElement);
  }

  const hintElement = document.createElement('p');
  hintElement.className =
    'mt-3 border-t border-slate-300/90 pt-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500';
  hintElement.textContent = tooltipData.actionHint;
  contentElements.push(hintElement);

  cardElement.append(...contentElements);

  return cardElement;
}

/**
 * Creates the camera popup card with availability-specific content.
 *
 * @param params Camera popup payload and resolved visual theme.
 * @returns Tooltip card ready to be inserted into the shared tooltip root.
 */
function createCameraTooltipCard(params: {
  tooltipData: MapApiCameraTooltip;
  stateTheme: CameraTooltipThemeItem;
}): HTMLElement {
  const { tooltipData, stateTheme } = params;
  const cardElement = createTooltipCard({
    surfaceColor: stateTheme.surfaceColor,
    borderColor: stateTheme.borderColor,
  });

  const titleElement = document.createElement('p');
  titleElement.className = 'text-sm font-semibold';
  titleElement.style.color = stateTheme.textColor;
  titleElement.textContent = tooltipData.title;

  const descriptionElement = document.createElement('p');
  descriptionElement.className = 'mt-3 text-sm leading-5';
  descriptionElement.style.color = stateTheme.textColor;
  descriptionElement.textContent = tooltipData.description;

  const actionElement = document.createElement('p');
  actionElement.className = 'mt-3 border-t pt-3 text-xs font-semibold uppercase tracking-[0.15em]';
  actionElement.style.borderColor = stateTheme.borderColor;
  actionElement.style.color = stateTheme.textColor;
  actionElement.textContent = tooltipData.actionText;

  cardElement.append(titleElement, descriptionElement, actionElement);

  return cardElement;
}

/**
 * Renders the structured field tooltip content inside the already prepared root container.
 *
 * @param params Tooltip DOM node, API payload and runtime state.
 */
export function renderFieldTooltipContent(params: {
  tooltipContent: HTMLElement;
  tooltipData: MapApiTooltip;
  state: MapState;
}): void {
  const { tooltipContent, tooltipData, state } = params;
  const stateTheme = getTooltipStateThemeFromState({
    state,
    debtState: tooltipData.debtState,
  });

  clearTooltipContent(tooltipContent);
  tooltipContent.append(
    createFieldTooltipCard({
      tooltipData,
      stateTheme,
    }),
  );
}

/**
 * Renders the camera hover popup content inside the shared tooltip root.
 *
 * @param params Tooltip DOM node, camera popup payload and runtime state.
 */
export function renderCameraTooltipContent(params: {
  tooltipContent: HTMLElement;
  tooltipData: MapApiCameraTooltip;
  state: MapState;
}): void {
  const { tooltipContent, tooltipData, state } = params;
  const stateTheme = getCameraTooltipThemeFromState({
    state,
    tooltipType: tooltipData.type,
  });

  clearTooltipContent(tooltipContent);
  tooltipContent.append(
    createCameraTooltipCard({
      tooltipData,
      stateTheme,
    }),
  );
}
